import {
  buildTokenRequest,
  getMsalInstanceForAccount,
  resolveActiveMsalAccount,
} from "../authConfig.js";
import { sessionAccountKey, sessionContextChanged } from "./Auth/sessionRecovery.js";

const silentFlights = new Map();
let interactiveFlight = null;

export function acquireSessionToken(msalInstance, scopes, account, forceRefresh = false) {
  const activeAccount = account || resolveActiveMsalAccount(msalInstance);
  if (!activeAccount) return Promise.resolve(null);
  const targetInstance = getMsalInstanceForAccount(activeAccount);
  const request = buildTokenRequest({ scopes, account: activeAccount, forceRefresh });
  const key = JSON.stringify([sessionAccountKey(activeAccount), request.authority, [...(request.scopes || [])].sort(), forceRefresh]);
  if (silentFlights.has(key)) return silentFlights.get(key);
  const promise = Promise.resolve().then(() => targetInstance.acquireTokenSilent(request)).then(response => {
    if (response?.account && sessionAccountKey(response.account) !== sessionAccountKey(activeAccount)) throw sessionContextChanged();
    return response;
  }).finally(() => silentFlights.delete(key));
  silentFlights.set(key, promise);
  return promise;
}

// Interactive acquisition is exclusively called from a user action. One popup
// may be active at a time, even when several components request recovery.
export function recoverSessionToken(msalInstance, account) {
  const activeAccount = account || resolveActiveMsalAccount(msalInstance);
  if (!activeAccount) return Promise.reject(Object.assign(new Error("Choose an account to sign in."), { errorCode: "no_account_error" }));
  const key = sessionAccountKey(activeAccount);
  if (interactiveFlight) return interactiveFlight.key === key
    ? interactiveFlight.promise
    : Promise.reject(sessionContextChanged());
  window.dispatchEvent(new Event("fieldprint:checkpoint-drafts"));
  const promise = Promise.resolve().then(() => getMsalInstanceForAccount(activeAccount).acquireTokenPopup(
    buildTokenRequest({ account: activeAccount, forceRefresh: true })
  )).then(response => {
    if (response?.account && sessionAccountKey(response.account) !== key) throw sessionContextChanged();
    return response;
  }).finally(() => { interactiveFlight = null; });
  interactiveFlight = { key, promise };
  return promise;
}

export async function getToken(
  msalInstance,
  scopes,
  account,
  forceRefresh = false
) {
  // A failed request must never unexpectedly open a popup or redirect an editor.
  const response = await acquireSessionToken(msalInstance, scopes, account, forceRefresh);
  return response?.accessToken ?? null;
}
